import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrgAuth } from '../contexts/OrgAuthContext';
import { getOrgConfig, updateOrgConfig } from '../api/orgApi';

// Event module components
import EventSetup           from '../components/admin/events/EventSetup';
import RegistrationWaitlist from '../components/admin/events/RegistrationWaitlist';
import AttendanceCheckIn    from '../components/admin/events/AttendanceCheckIn';
import EventCalendar        from '../components/admin/events/EventCalendar';
import EventContentMedia    from '../components/admin/events/EventContentMedia';
import NotificationBell     from '../components/shared/NotificationBell';
import EventQRScannerModal  from '../components/admin/events/EventQRScannerModal';

// Programme module components
import GovernmentSchemes      from '../components/admin/programmes/GovernmentSchemes';
import OrganisationProgrammes from '../components/admin/programmes/OrganisationProgrammes';
import JobBoard               from '../components/admin/programmes/JobBoard';
import SupportNetwork         from '../components/admin/programmes/SupportNetwork';
import Cases                  from '../components/admin/support/Cases';
import FinancialAssistance    from '../components/admin/programmes/FinancialAssistance';

// Communications module components
import EmailCampaigns    from '../components/admin/communications/EmailCampaigns';
import PushCampaigns     from '../components/admin/communications/PushCampaigns';
import WhatsAppCampaigns from '../components/admin/communications/WhatsAppCampaigns';
import CampaignListing   from '../components/admin/communications/CampaignListing';

// Reports module components
import QueryBuilder        from '../components/admin/reports/QueryBuilder';
import AnalyticsDashboard  from '../components/admin/reports/AnalyticsDashboard';
import EngagementReport    from '../components/admin/reports/EngagementReport';
import VolunteerReport     from '../components/admin/reports/VolunteerReport';

// Bespoke AI module components
import CommunityAI         from '../components/admin/bespokeai/CommunityAI';
import CommunicationsAI    from '../components/admin/bespokeai/CommunicationsAI';
import EventsAI            from '../components/admin/bespokeai/EventsAI';
import ReportsAI           from '../components/admin/bespokeai/ReportsAI';

// Community module components
import VolunteerProfiles  from '../components/admin/community/VolunteerProfiles';

import MemberProfiles     from '../components/admin/community/MemberProfiles';
import MemberSegmentation    from '../components/admin/community/MemberSegmentation';
import VolunteerSegmentation from '../components/admin/community/VolunteerSegmentation';

// Core module components
import AboutUsEditor      from '../components/admin/core/AboutUsEditor';
import ContactInfoEditor  from '../components/admin/core/ContactInfoEditor';
import OfficeHoursEditor  from '../components/admin/core/OfficeHoursEditor';
import ImageManager       from '../components/admin/core/ImageManager';
import TypographyEditor   from '../components/admin/core/TypographyEditor';
import CustomCSSEditor    from '../components/admin/core/CustomCSSEditor';
import BrandingEditor     from '../components/admin/core/BrandingEditor';

// Donor Management module components
import DonorProfiles  from '../components/admin/donors/DonorProfiles';
import DonorContacts  from '../components/admin/donors/DonorContacts';
import DonorGrants    from '../components/admin/donors/DonorGrants';
import DonorComms     from '../components/admin/donors/DonorComms';
import DonorTimeline from '../components/admin/donors/DonorTimeline';
import '../styles/donors.css';

// Dashboard module components
import AdminHome  from '../components/admin/dashboard/AdminHome';
import AllModules from '../components/admin/dashboard/AllModules';

// Placeholder for modules not yet built
function ComingSoon({ module }) {
  return (
    <div className="bb-coming-soon">
      <div className="bb-coming-soon-icon">🔧</div>
      <h2>{module}</h2>
      <p>This module is under development and will be available in the next phase.</p>
      <div className="bb-coming-soon-badge">Coming in Phase 2</div>
    </div>
  );
}

const NEW_DEFAULT_COLORS = { primary: '#3A7D44', secondary: '#B8D9A0', accent: '#EBF5E4', background: '#F5F0E8', text: '#2D3A2E' };

// Known legacy default palettes — any org still on one of these gets migrated to the new defaults.
const LEGACY_PALETTES = [
  { primary: '#9B59B6', secondary: '#1ABC9C', accent: '#F5EEF8', background: '#FFFFFF', text: '#2C3E50' },
  { primary: '#0D9488', secondary: '#0D9488', accent: '#F0FDFA', background: '#FFFFFF', text: '#333333' },
  { primary: '#6B46C1', secondary: '#0D9488', accent: '#F3F0FF', background: '#FFFFFF', text: '#333333' },
];

function migrateColors(saved) {
  if (!saved) return NEW_DEFAULT_COLORS;
  const isLegacy = LEGACY_PALETTES.some(p =>
    Object.keys(p).every(k => !saved[k] || saved[k] === p[k])
  );
  return isLegacy ? NEW_DEFAULT_COLORS : { ...NEW_DEFAULT_COLORS, ...saved };
}

const DEFAULT_CONFIG = {
  branding: {
    name: 'My Organisation',
    logo: { url: '', width: 200, height: 80 },
    colors: NEW_DEFAULT_COLORS,
    typography: { headingFont: 'Familjen Grotesk, sans-serif', bodyFont: 'Wix Madefor Text, sans-serif', fontSize: 'medium' },
    images: { hero: '', gallery: [] },
    customCSS: ''
  },
  contact: { address: '', phone: '', email: '', website: '', socialMedia: { facebook: '', instagram: '', twitter: '', linkedin: '', whatsapp: '' } },
  aboutUs: { mission: '', vision: '', description: '', yearFounded: '', registrationNumber: '' },
  officeHours: {
    regularHours: [
      { day: 'Monday',    open: '09:00', close: '17:00', isClosed: false },
      { day: 'Tuesday',   open: '09:00', close: '17:00', isClosed: false },
      { day: 'Wednesday', open: '09:00', close: '17:00', isClosed: false },
      { day: 'Thursday',  open: '09:00', close: '17:00', isClosed: false },
      { day: 'Friday',    open: '09:00', close: '17:00', isClosed: false },
      { day: 'Saturday',  open: '09:00', close: '13:00', isClosed: false },
      { day: 'Sunday',    open: '09:00', close: '13:00', isClosed: true  },
    ],
    holidayClosures: [],
    customClosures:  [],
    notes: '',
  }
};

// ─── Navigation groups (two-row tab bar) ────────────────────────────────────
const NAV_GROUPS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: (
      <svg viewBox="0 0 16 16" fill="none">
        <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    items: [
      { groupId: 'dashboard', tabId: 'adminHome',  label: 'Admin Home'  },
      { groupId: 'dashboard', tabId: 'allModules', label: 'All Modules' },
    ]
  },
  {
    id: 'core',
    label: 'Core',
    icon: (
      <svg viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="8" cy="8" r="2" fill="currentColor"/>
      </svg>
    ),
    items: [
      { groupId: 'core', tabId: 'branding',   label: 'Brand Identity'   },
      { groupId: 'core', tabId: 'aboutUs',    label: 'About Us'         },
      { groupId: 'core', tabId: 'contact',      label: 'Contact Info'     },
      { groupId: 'core', tabId: 'officeHours', label: 'Office Hours'     },
      { groupId: 'core', tabId: 'images',      label: 'Image Management' },
      { groupId: 'core', tabId: 'typography', label: 'Typography'       },
      { groupId: 'core', tabId: 'customCSS',  label: 'Custom CSS'       },
    ]
  },
  {
    id: 'programmes',
    label: 'Programmes Management',
    moduleKey: 'programmes',
    icon: (
      <svg viewBox="0 0 16 16" fill="none">
        <rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 6h6M5 9h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    items: [
      { groupId: 'programmes', tabId: 'govSchemes',    label: 'Government Programmes'   },
      { groupId: 'programmes', tabId: 'orgProgrammes', label: 'Organisation Programmes' },
    ]
  },
  {
    id: 'community',
    label: 'Community Management',
    moduleKey: 'community',
    icon: (
      <svg viewBox="0 0 16 16" fill="none">
        <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="11" cy="7" r="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M1 13c0-2.5 2-4 5-4s5 1.5 5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M11 11c1.5 0 3 .8 3 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    items: [
      { groupId: 'community', tabId: 'volunteerProfiles',     label: 'Volunteer Profiles'     },
      { groupId: 'community', tabId: 'volunteerSegmentation', label: 'Volunteer Segmentation' },
      { groupId: 'community', tabId: 'memberProfiles',        label: 'Member Profiles'        },
      { groupId: 'community', tabId: 'memberSegmentation',    label: 'Member Segmentation'    },
    ]
  },
  {
    id: 'supportmgmt',
    label: 'Support Management',
    moduleKey: 'supportmgmt',
    icon: (
      <svg viewBox="0 0 16 16" fill="none">
        <circle cx="5" cy="4.5" r="1.8" stroke="currentColor" strokeWidth="1.4"/>
        <circle cx="11" cy="4.5" r="1.8" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M1.5 13c0-1.9 1.6-3.5 3.5-3.5s3.5 1.6 3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M8.5 11.5c.5-1.2 1.7-2 3-2 1.9 0 3.5 1.6 3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M8 9.5c.9-.3 1.7-.5 2.5-.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
    items: [
      { groupId: 'supportmgmt', tabId: 'jobBoard',       label: 'Job Board'       },
      { groupId: 'supportmgmt', tabId: 'supportNetwork', label: 'Support Network' },
      { groupId: 'supportmgmt', tabId: 'cases',          label: 'Cases'           },
    ]
  },
  {
    id: 'events',
    label: 'Events Management',
    moduleKey: 'events',
    icon: (
      <svg viewBox="0 0 16 16" fill="none">
        <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 2v2M11 2v2M2 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    items: [
      { groupId: 'events', tabId: 'eventSetup',    label: 'Event Setup'           },
      { groupId: 'events', tabId: 'registrations', label: 'Registration & Waitlist'},
      { groupId: 'events', tabId: 'attendance',    label: 'Attendance & Check-In' },
      { groupId: 'events', tabId: 'calendar',      label: 'Event Calendar'        },
      { groupId: 'events', tabId: 'mediaContent',  label: 'Content & Media'       },
    ]
  },
  {
    id: 'communications',
    label: 'Communications Management',
    moduleKey: 'communications',
    icon: (
      <svg viewBox="0 0 16 16" fill="none">
        <path d="M2 3h12a1 1 0 011 1v7a1 1 0 01-1 1H9l-3 2v-2H2a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      </svg>
    ),
    items: [
      { groupId: 'communications', tabId: 'emailCampaigns',    label: 'Email Campaigns'    },
      { groupId: 'communications', tabId: 'pushCampaigns',     label: 'Push Notifications' },
      { groupId: 'communications', tabId: 'whatsappCampaigns', label: 'WhatsApp Campaigns' },
      { groupId: 'communications', tabId: 'campaignListing',   label: 'Campaigns Listing'  },
    ]
  },
  {
    id: 'reports',
    label: 'Reports Management',
    moduleKey: 'reports',
    icon: (
      <svg viewBox="0 0 16 16" fill="none">
        <rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 10V8M8 10V6M11 10V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    items: [
      { groupId: 'reports', tabId: 'queryBuilder',       label: 'Query Builder'       },
      { groupId: 'reports', tabId: 'analyticsDashboard', label: 'Analytics Dashboard' },
      { groupId: 'reports', tabId: 'engagementReport',   label: 'Engagement Report'   },
      { groupId: 'reports', tabId: 'volunteerReport',    label: 'Volunteer Report'    },
    ]
  },
  {
    id: 'donors',
    moduleKey: 'donors',
    label: 'Donor Management',
    icon: (
      <svg viewBox="0 0 16 16" fill="none">
        <path d="M8 2C9.1 2 10 2.9 10 4s-.9 2-2 2-2-.9-2-2 .9-2 2-2z" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M3 13c0-2.2 2-4 5-4s5 1.8 5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M12 7l1.5 1.5M13.5 7L12 8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    items: [
      { groupId: 'donors', tabId: 'donorProfiles',  label: 'Donor Profiles'   },
      { groupId: 'donors', tabId: 'donorContacts',  label: 'Contact CRM'      },
      { groupId: 'donors', tabId: 'donorGrants',    label: 'Grants & KPIs'    },
      { groupId: 'donors', tabId: 'donorComms',     label: 'Comms Log'        },
      { groupId: 'donors', tabId: 'donorTimeline',  label: 'Timeline & Alerts'},
    ]
  },
  {
    id: 'bespokeai',
    label: 'Bespoke AI',
    moduleKey: 'bespokeai',
    icon: (
      <svg viewBox="0 0 16 16" fill="none">
        <path d="M8 2l1.5 3.5L13 7l-3.5 1.5L8 12l-1.5-3.5L3 7l3.5-1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      </svg>
    ),
    items: [
      { groupId: 'bespokeai', tabId: 'communityAI',      label: 'Community AI'      },
      { groupId: 'bespokeai', tabId: 'communicationsAI', label: 'Communications AI' },
      { groupId: 'bespokeai', tabId: 'eventsAI',         label: 'Events AI'         },
      { groupId: 'bespokeai', tabId: 'reportsAI',        label: 'Reports AI'        },
    ]
  },
];

function AdminPortal() {
  const { orgId } = useParams();
  const { currentUser, logout } = useOrgAuth();
  const navigate = useNavigate();

  const [activeGroupId, setActiveGroupId] = useState('dashboard');
  const [activeTab,     setActiveTab]     = useState('adminHome');

  const [orgConfig,    setOrgConfig]    = useState(DEFAULT_CONFIG);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [userMenuOpen,   setUserMenuOpen]   = useState(false);
  const [showQRScanner,  setShowQRScanner]  = useState(false);
  const userMenuRef  = useRef(null);
  const orgConfigRef = useRef(DEFAULT_CONFIG); // always current — avoids stale closure in auto-save callbacks

  useEffect(() => { orgConfigRef.current = orgConfig; }, [orgConfig]);

  // Load org config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        const config = await getOrgConfig(orgId);
        if (config) {
          setOrgConfig(prev => ({
            ...DEFAULT_CONFIG,
            ...config,
            branding:     { ...DEFAULT_CONFIG.branding,     ...(config.branding     || {}), colors: migrateColors(config.branding?.colors) },
            contact:      { ...DEFAULT_CONFIG.contact,      ...(config.contact      || {}) },
            aboutUs:      { ...DEFAULT_CONFIG.aboutUs,      ...(config.aboutUs      || {}) },
            officeHours: (() => {
              const saved = config.officeHours || {};
              // New format already has regularHours array — use as-is
              if (Array.isArray(saved.regularHours)) {
                return {
                  ...DEFAULT_CONFIG.officeHours,
                  ...saved,
                  regularHours:    saved.regularHours    || DEFAULT_CONFIG.officeHours.regularHours,
                  holidayClosures: saved.holidayClosures || [],
                  customClosures:  saved.customClosures  || [],
                  notes:           saved.notes           || '',
                };
              }
              // Old format — pass raw; OfficeHoursEditor handles migration internally
              return saved;
            })(),
          }));
        }
      } catch (err) {
        console.error('Error loading org config:', err);
      } finally {
        setLoading(false);
      }
    };
    if (orgId) loadConfig();
  }, [orgId]);

  const handleConfigUpdate = useCallback((section, newData) => {
    setOrgConfig(prev => ({
      ...prev,
      [section]: typeof newData === 'string' ? newData : { ...prev[section], ...newData }
    }));
  }, []);

  const saveChanges = async () => {
    setSaving(true);
    try {
      await updateOrgConfig(orgId, orgConfig);
      setSavedMessage('Changes saved successfully!');
    } catch (err) {
      console.error('Error saving config:', err);
      setSavedMessage('Error saving changes. Please try again.');
    } finally {
      setSaving(false);
      setTimeout(() => setSavedMessage(''), 3000);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate(`/BespokeBridge/${orgId}/admin/login`);
  };

  if (loading) {
    return (
      <div className="bb-loading-screen">
        <div className="bb-loading-spinner"></div>
        <p>Loading your portal...</p>
      </div>
    );
  }

  const brandColors = orgConfig.branding?.colors || {};
  const orgCssVars = {
    '--org-primary':    brandColors.primary    || '#3A7D44',
    '--org-secondary':  brandColors.secondary  || '#B8D9A0',
    '--org-accent':     brandColors.accent     || '#EBF5E4',
    '--org-background': brandColors.background || '#F5F0E8',
    '--org-text':       brandColors.text       || '#2D3A2E',
  };


  // ── Module gating: filter nav groups by enabledModules ────────────────────
  const enabledModules = orgConfig.enabledModules;
  // Bespoke AI sub-tools (communityAI, ...) are switched off individually by the SuperAdmin.
  // Only an explicit `false` hides one, so orgs configured before sub-modules existed keep all four.
  const visibleGroups = NAV_GROUPS
    .filter(g => {
      if (!g.moduleKey) return true;
      if (!enabledModules) return true;
      return !!enabledModules[g.moduleKey];
    })
    .map(g => (
      g.id === 'bespokeai' && enabledModules
        ? { ...g, items: g.items.filter(i => enabledModules[i.tabId] !== false) }
        : g
    ))
    .filter(g => g.items.length > 0);

  // If currently active group became invisible (module disabled), fall back to dashboard
  const activeGroupIsVisible = visibleGroups.some(g => g.id === activeGroupId);
  const effectiveGroupId = activeGroupIsVisible ? activeGroupId : 'dashboard';

  // ── Flat switch on activeTab ───────────────────────────────────────────────
  const renderActiveEditor = () => {
    switch (activeTab) {
      // DASHBOARD
      case 'adminHome':
        return (
          <AdminHome
            orgId={orgId}
            heroImageUrl={orgConfig.branding?.images?.hero || ''}
            onGoToImages={() => { setActiveGroupId('core'); setActiveTab('images'); }}
          />
        );
      case 'allModules':
        return (
          <AllModules
            onNavigate={(groupId, tabId) => {
              setActiveGroupId(groupId);
              setActiveTab(tabId);
            }}
            orgId={orgId}
            currentUser={currentUser}
            enabledModules={enabledModules}
          />
        );

      // CORE
      case 'branding':
        return (
          <BrandingEditor
            branding={orgConfig.branding}
            onUpdate={(data) => handleConfigUpdate('branding', data)}
            onAutoSave={async (newBranding) => {
              const current = orgConfigRef.current;
              await updateOrgConfig(orgId, { ...current, branding: newBranding });
              setSavedMessage('Logo saved!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
            orgId={orgId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
          />
        );
      case 'aboutUs':
        return (
          <AboutUsEditor
            aboutUs={orgConfig.aboutUs}
            onUpdate={(data) => handleConfigUpdate('aboutUs', data)}
            orgId={orgId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
          />
        );
      case 'contact':
        return (
          <ContactInfoEditor
            contact={orgConfig.contact}
            onUpdate={(data) => handleConfigUpdate('contact', data)}
            orgId={orgId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
          />
        );
      case 'officeHours':
        return (
          <OfficeHoursEditor
            officeHours={orgConfig.officeHours}
            onUpdate={(data) => handleConfigUpdate('officeHours', data)}
            orgId={orgId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
          />
        );
      case 'images':
        return (
          <ImageManager
            images={orgConfig.branding?.images}
            onUpdate={(data) => handleConfigUpdate('branding', { ...orgConfig.branding, images: data })}
            onAutoSave={async (newImages) => {
              const current = orgConfigRef.current;
              await updateOrgConfig(orgId, {
                ...current,
                branding: { ...current.branding, images: newImages },
              });
              setSavedMessage('Images saved!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
            orgId={orgId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
          />
        );
      case 'typography':
        return (
          <TypographyEditor
            typography={orgConfig.branding?.typography}
            onUpdate={(data) => handleConfigUpdate('branding', { ...orgConfig.branding, typography: data })}
            orgId={orgId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
          />
        );
      case 'customCSS':
        return (
          <CustomCSSEditor
            customCSS={orgConfig.branding?.customCSS}
            onUpdate={(css) => handleConfigUpdate('branding', { ...orgConfig.branding, customCSS: css })}
            orgId={orgId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
          />
        );

      // PROGRAMMES
      case 'govSchemes':
        return <GovernmentSchemes orgId={orgId} />;
      case 'orgProgrammes':
        return <OrganisationProgrammes orgId={orgId} />;
      case 'jobBoard':
        return <JobBoard orgId={orgId} />;
      case 'supportNetwork':
        return <SupportNetwork orgId={orgId} />;
      case 'cases':
        return <Cases orgId={orgId} />;
      case 'financialAid':
        return <FinancialAssistance orgId={orgId} />;

      // COMMUNITY
      case 'volunteerProfiles':
        return <VolunteerProfiles orgId={orgId} adminId={currentUser?.uid} adminName={currentUser?.name || currentUser?.email} />;

      case 'memberProfiles':
        return <MemberProfiles orgId={orgId} adminId={currentUser?.uid} />;
      case 'memberSegmentation':
        return <MemberSegmentation orgId={orgId} adminId={currentUser?.uid} />;
      case 'volunteerSegmentation':
        return <VolunteerSegmentation orgId={orgId} adminId={currentUser?.uid} />;

      // EVENTS
      case 'eventSetup':
        return (
          <EventSetup
            orgId={orgId}
            adminId={currentUser?.uid}
            adminName={currentUser?.name || currentUser?.email}
            onNavigateToEmail={() => { setActiveGroupId('communications'); setActiveTab('emailCampaigns'); }}
            onNavigateToPush={() => { setActiveGroupId('communications'); setActiveTab('pushCampaigns'); }}
            onNavigateToWhatsApp={() => { setActiveGroupId('communications'); setActiveTab('whatsappCampaigns'); }}
          />
        );
      case 'registrations':
        return <RegistrationWaitlist orgId={orgId} adminId={currentUser?.uid} adminName={currentUser?.name || currentUser?.email} />;
      case 'attendance':
        return <AttendanceCheckIn orgId={orgId} adminId={currentUser?.uid} />;
      case 'calendar':
        return (
          <EventCalendar
            orgId={orgId}
            adminId={currentUser?.uid}
            onNavigateToRegistrations={() => setActiveTab('registrations')}
          />
        );
      case 'mediaContent':
        return <EventContentMedia orgId={orgId} adminId={currentUser?.uid} adminName={currentUser?.name || currentUser?.email} />;

      // COMMUNICATIONS
      case 'emailCampaigns':
        return <EmailCampaigns orgId={orgId} adminId={currentUser?.uid} adminName={currentUser?.name || currentUser?.email} />;
      case 'pushCampaigns':
        return <PushCampaigns orgId={orgId} adminId={currentUser?.uid} adminName={currentUser?.name || currentUser?.email} />;
      case 'whatsappCampaigns':
        return <WhatsAppCampaigns orgId={orgId} adminId={currentUser?.uid} adminName={currentUser?.name || currentUser?.email} />;
      case 'campaignListing':
        return (
          <CampaignListing
            orgId={orgId}
            adminId={currentUser?.uid}
            onNavigateToChannel={(ch) => {
              const map = { email: 'emailCampaigns', push: 'pushCampaigns', whatsapp: 'whatsappCampaigns' };
              setActiveTab(map[ch] || 'emailCampaigns');
            }}
          />
        );

      // REPORTS
      case 'queryBuilder':
        return <QueryBuilder orgId={orgId} adminId={currentUser?.uid} />;
      case 'analyticsDashboard':
        return <AnalyticsDashboard orgId={orgId} adminId={currentUser?.uid} />;
      case 'engagementReport':
        return <EngagementReport orgId={orgId} adminId={currentUser?.uid} />;
      case 'volunteerReport':
        return <VolunteerReport orgId={orgId} adminId={currentUser?.uid} />;

      // DONOR MANAGEMENT
      case 'donorProfiles':
        return <DonorProfiles orgId={orgId} />;
      case 'donorContacts':
        return <DonorContacts orgId={orgId} />;
      case 'donorGrants':
        return <DonorGrants orgId={orgId} />;
      case 'donorComms':
        return <DonorComms orgId={orgId} />;
      case 'donorTimeline':
        return <DonorTimeline orgId={orgId} />;

      // BESPOKE AI
      case 'communityAI':
        return <CommunityAI orgId={orgId} adminId={currentUser?.uid} />;
      case 'communicationsAI':
        return (
          <CommunicationsAI
            orgId={orgId}
            adminId={currentUser?.uid}
            onUseInEmail={() => setActiveTab('emailCampaigns')}
            onUseInWhatsApp={() => setActiveTab('whatsappCampaigns')}
            onUseInPush={() => setActiveTab('pushCampaigns')}
          />
        );
      case 'eventsAI':
        return <EventsAI orgId={orgId} adminId={currentUser?.uid} />;
      case 'reportsAI':
        return <ReportsAI orgId={orgId} adminId={currentUser?.uid} />;


      default:
        return <ComingSoon module={activeTab || 'Module'} />;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="bb-admin" style={orgCssVars}>
      {/* ── Top Header ──────────────────────────────────────────────────── */}
      <header className="bb-admin-header">
        <div className="bb-admin-header-brand">
          <div className="bb-brand-row">
            <span className="bb-brand-name">BespokeBridge</span>
            <span className="bb-portal-type">Admin Portal</span>
            <span className="bb-org-id-badge">{orgId}</span>
          </div>
        </div>

        <div className="bb-admin-header-user">
          {savedMessage && (
            <span className={`bb-save-toast ${savedMessage.includes('Error') ? 'error' : 'success'}`}>
              {savedMessage}
            </span>
          )}
          {activeGroupId === 'core' && activeTab !== 'images' && activeTab !== 'officeHours' && (
            <button className="bb-save-btn" onClick={saveChanges} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
          <button
            className="bb-scan-qr-pill"
            onClick={() => setShowQRScanner(true)}
            title="Scan member QR code for event attendance"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ flexShrink: 0 }}>
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/>
              <path d="M14 14h3v3M17 17h3M17 20v1M20 14v3"/>
            </svg>
            <span>Scan QR Code</span>
          </button>
          <NotificationBell orgId={orgId} recipientId="ADMIN" accentColor={brandColors.primary || '#3A7D44'} />
          <div className="bb-user-menu" ref={userMenuRef}>
            <button
              className="bb-user-menu-btn"
              onClick={() => setUserMenuOpen(v => !v)}
            >
              <span className="bb-user-avatar">
                {(currentUser?.displayName || currentUser?.name || currentUser?.email || 'A').charAt(0).toUpperCase()}
              </span>
              <span className="bb-user-name">
                {currentUser?.displayName || currentUser?.name || currentUser?.email}
              </span>
              <span style={{ fontSize: 10, opacity: 0.6 }}>▾</span>
            </button>
            {userMenuOpen && (
              <div className="bb-user-dropdown">
                <div className="bb-user-dropdown-info">
                  <div className="bb-user-dropdown-name">
                    {currentUser?.displayName || currentUser?.name || currentUser?.email}
                  </div>
                  <div className="bb-user-dropdown-email">{currentUser?.email}</div>
                  {currentUser?.role && (
                    <div className="bb-user-dropdown-role">{currentUser.role}</div>
                  )}
                </div>
                <hr className="bb-user-dropdown-divider" />
                <button className="bb-user-dropdown-item bb-user-dropdown-logout" onClick={handleLogout}>
                  ⎋ Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Sub-header: module groups (row 1) + tool pills (row 2) ──────── */}
      <div className="bb-tab-bar">
        <div className="bb-tab-bar-groups">
          {visibleGroups.map(group => (
            <button
              key={group.id}
              data-group-id={group.id}
              className={`bb-tab-group-btn${effectiveGroupId === group.id ? ' selected' : ''}`}
              onClick={() => {
                setActiveGroupId(group.id);
                setActiveTab(group.items[0].tabId);
              }}
            >
              <span className="bb-tab-group-icon">{group.icon}</span>
              <span className="bb-tab-group-label">{group.label}</span>
            </button>
          ))}
        </div>
        <div className="bb-tab-bar-items">
          {visibleGroups.find(g => g.id === effectiveGroupId)?.items.map(item => (
            <button
              key={item.tabId}
              className={`bb-tab-item-btn${activeTab === item.tabId ? ' selected' : ''}`}
              onClick={() => setActiveTab(item.tabId)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Main content ────────────────────────────────────────────────── */}
      <main className={`bb-admin-editor${activeTab === 'adminHome' ? ' bb-admin-editor--hero' : ''}`}>
        {renderActiveEditor()}
      </main>

      {/* ── QR Scanner Modal ─────────────────────────────────────────────── */}
      {showQRScanner && (
        <EventQRScannerModal
          orgId={orgId}
          adminId={currentUser?.uid || currentUser?.id || 'admin'}
          onClose={() => setShowQRScanner(false)}
        />
      )}
    </div>
  );
}

export default AdminPortal;
